import {OctAuthKeysEnum} from '@core/auth/auth.enum';
import {Directive, effect, inject, signal} from '@angular/core';
import {CookieService} from 'ngx-cookie-service';
import {User, UserService} from '@core/services';
import {map} from 'rxjs/operators';
import {WORKFLOW_USER_KEY} from '@shared/constants/storage-keys';
import decode from 'jwt-decode';
import {PermissionAbstract} from '@services/permission/permission.abstract';
import {DASHBOARD_ROUTE_KEY, LANGUAGE_DASHBOARD_ROUTE_KEY, MENU_LIST} from './home.config';
import {BrowserStorageService} from '@services/browser-storage/browser-storage.service';
import {DASHBOARD_STORAGE_KEY, isCompanyAttributeEnabled, LANGUAGE_STORAGE_KEY} from '@core/guards';
import {toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {MenuModel} from '@shared/components/menu';
import {NotificationsUpdateService} from '@services/notifications/notifications-update.service';

interface MenuDashboardPermissions {
  allowDashboard?: boolean;
  allowLanguage?: boolean;
}

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class MenuListPermission extends PermissionAbstract {
  private readonly _notificationsUpdateService = inject(NotificationsUpdateService);
  isAllowedDashboard = toSignal(isCompanyAttributeEnabled(DASHBOARD_STORAGE_KEY, 'isDashboard'), {initialValue: false});
  isAllowedLanguageDashboard = toSignal(isCompanyAttributeEnabled(LANGUAGE_STORAGE_KEY, 'isLanguageDashboard'), {initialValue: false});
  menuList = signal(MENU_LIST);

  protected _updateMenuPermissions({allowDashboard = false, allowLanguage = false}: MenuDashboardPermissions = {}) {
    this.menuList.update(menu =>
      menu.map(item => ({
        ...item,
        isHidden: this._shouldHideMenuItem(item, allowDashboard, allowLanguage)
      }))
    );
  }

  protected _setUnreadNotificationsCount() {
    effect(() => {
      this.menuList.update(menu =>
        menu.map(item => {
          if (item.item.toLocaleLowerCase() === 'notifications') {
            return {
              ...item,
              badgeCount: this._notificationsUpdateService.unreadCount()
            };
          }
          return item;
        })
      );
    });
  }

  private _shouldHideMenuItem(item: MenuModel, allowDashboard: boolean, allowLanguage: boolean): boolean {
    return (
      this._lacksPermission(item) || (this._isDashboard(item) && !allowDashboard) || (this._isLanguageDashboard(item) && !allowLanguage)
    );
  }

  private _lacksPermission(item: MenuModel): boolean {
    return !!item.permission && !this.permission.includes(item.permission);
  }

  private _isDashboard(item: MenuModel): boolean {
    return item.routerLink === DASHBOARD_ROUTE_KEY;
  }

  private _isLanguageDashboard(item: MenuModel): boolean {
    return item.routerLink.startsWith(LANGUAGE_DASHBOARD_ROUTE_KEY);
  }
}

const getValueFromCookieStorage = (key = OctAuthKeysEnum.UserToken): string => {
  const cookieService = inject(CookieService);
  return cookieService.get(key);
};

export const updateCurrentUser = (userService: UserService) => {
  const storedId = getValueFromCookieStorage(OctAuthKeysEnum.UserId);
  const localStorageService = inject(BrowserStorageService);
  if (storedId) {
    return userService.getById(storedId).pipe(
      map(user => {
        localStorageService.set(WORKFLOW_USER_KEY, JSON.stringify(user));
        userService.currentUser.next(user);
        return user;
      })
    );
  }
  return of({} as User);
};

export const getDecodedTokenPermissions = (): string[] => {
  const userToken = getValueFromCookieStorage();
  if (userToken) {
    return decode(userToken).permissions;
  }
  return [];
};
